import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import creditSubmissionApi, { CreditSubmission } from '../../api/creditSubmissionApi';
import creditApi from '../../api/creditApi';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../components/credit/submission/detail/utils';
import Layout from '../../components/Layout';

// Detail Section Components
import DetailHeader from '../../components/credit/submission/detail/DetailHeader';
import DetailSection1 from '../../components/credit/submission/detail/DetailSection1';
import DetailSection2 from '../../components/credit/submission/detail/DetailSection2';
import DetailSection3 from '../../components/credit/submission/detail/DetailSection3';
import DetailSection4 from '../../components/credit/submission/detail/DetailSection4';
import DetailSection5 from '../../components/credit/submission/detail/DetailSection5';
import DetailSection6 from '../../components/credit/submission/detail/DetailSection6';
import DetailSection7 from '../../components/credit/submission/detail/DetailSection7';
import DetailSection8 from '../../components/credit/submission/detail/DetailSection8';
import DetailSection9 from '../../components/credit/submission/detail/DetailSection9';
import DetailSection10 from '../../components/credit/submission/detail/DetailSection10';
import DetailSection11 from '../../components/credit/submission/detail/DetailSection11';
import DetailMetadata from '../../components/credit/submission/detail/DetailMetadata';

export default function CreditSubmissionDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError } = useToast();
  const submissionId = searchParams.get('id');
  const from = searchParams.get('from'); // 'application-list' or 'approval-list'

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState<CreditSubmission | null>(null);

  const loadSubmission = useCallback(async () => {
    try {
      setLoading(true);
      // Try to load from credit_submissions first
      try {
        const data = await creditSubmissionApi.getById(Number(submissionId));
        setSubmission(data);
      } catch (submissionError) {
        // If not found in credit_submissions, try credit_applications
        try {
          const appData = await creditApi.getById(Number(submissionId));
          // Transform credit_application to CreditSubmission format
          const transformedData: CreditSubmission = {
            id: appData.id,
            submission_number:
              (appData as any).submission_number || (appData as any).application_number || '',
            status: appData.status as any,
            debtor_id: appData.debtor_id,
            application_type: (appData as any).credit_type || '',
            credit_system: '',
            debtor_type: 'individual',
            is_entrepreneur: false,
            debtor_profile: '',
            loan_purpose: appData.loan_purpose || '',
            requested_amount: appData.requested_amount,
            debtor_name: appData.debtor_name || '',
            id_number: '',
            birth_place: '',
            birth_date: '',
            gender: '',
            marital_status: '',
            education: '',
            photo_url: '',
            phone: '',
            mother_maiden_name: '',
            emergency_contact: '',
            bank_relationship: '',
            last_facility: '',
            last_credit_quality: '',
            last_arrears: 0,
            slik_document_url: '',
            slik_effective_plafon: 0,
            slik_outstanding: 0,
            slik_worst_quality: '',
            slik_data_month: '',
            slik_position_date: '',
            address: '',
            village: '',
            district: '',
            city: '',
            latitude: null,
            longitude: null,
            relations: [],
            business_documents: [],
            collateral_info: {},
            collateral_documents: [],
            collateral_latitude: null,
            collateral_longitude: null,
            appraisal_data: {},
            credit_history: [],
            financial_projection: {},
            current_step: 1,
            scoring_model_id: null,
            created_at: appData.created_at,
            updated_at: appData.updated_at,
          };
          setSubmission(transformedData);
        } catch (appError) {
          throw submissionError; // Throw original error if both fail
        }
      }
    } catch (error: any) {
      console.error('Error loading submission:', error);
      showError(error?.response?.data?.error || error?.message || 'Gagal memuat data pengajuan');
      navigate('/credit/applications');
    } finally {
      setLoading(false);
    }
  }, [submissionId, navigate, showError]);

  useEffect(() => {
    if (submissionId) {
      loadSubmission();
    } else {
      showError('ID pengajuan tidak ditemukan');
      navigate('/credit/approval');
    }
  }, [submissionId, loadSubmission, showError, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Create a printable version of the page
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Get the content from the detail page (excluding buttons and navigation)
    const contentElement = document.querySelector('.space-y-6');
    if (!contentElement) return;

    // Clone the content to avoid modifying the original
    const clonedContent = contentElement.cloneNode(true) as HTMLElement;

    // Remove no-print elements from cloned content
    const noPrintElements = clonedContent.querySelectorAll('.no-print');
    noPrintElements.forEach(el => el.remove());

    const printStyles = `
      <style>
        @page {
          size: A4;
          margin: 1.5cm;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          font-size: 11px;
          line-height: 1.6;
          color: #333;
          padding: 20px;
        }
        h1 {
          font-size: 24px;
          margin-bottom: 10px;
          color: #1e3a5f;
        }
        h2 {
          font-size: 18px;
          margin-top: 20px;
          margin-bottom: 10px;
          color: #1e3a5f;
        }
        h3, h4 {
          font-size: 14px;
          margin-top: 15px;
          margin-bottom: 8px;
          color: #374151;
        }
        .card {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          margin-bottom: 15px;
          padding: 15px;
          page-break-inside: avoid;
        }
        .card-header {
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 10px;
          margin-bottom: 15px;
        }
        .card-title {
          font-size: 16px;
          font-weight: 600;
          color: #1e3a5f;
        }
        .card-content {
          padding-top: 0;
        }
        label {
          display: block;
          font-size: 10px;
          color: #6b7280;
          margin-bottom: 4px;
          font-weight: 500;
        }
        p {
          margin-bottom: 8px;
          font-size: 11px;
        }
        .grid {
          display: grid;
          gap: 12px;
        }
        .md\\:grid-cols-2 {
          grid-template-columns: repeat(2, 1fr);
        }
        .md\\:grid-cols-3 {
          grid-template-columns: repeat(3, 1fr);
        }
        .md\\:grid-cols-4 {
          grid-template-columns: repeat(4, 1fr);
        }
        .space-y-4 > * + * {
          margin-top: 16px;
        }
        .space-y-6 > * + * {
          margin-top: 24px;
        }
        .bg-gray-50, .bg-green-50, .bg-red-50, .bg-blue-50 {
          background-color: #f9fafb !important;
        }
        .border {
          border: 1px solid #e5e7eb;
        }
        .rounded-lg {
          border-radius: 6px;
        }
        .p-4 {
          padding: 16px;
        }
        .mt-1 {
          margin-top: 4px;
        }
        .mb-2 {
          margin-bottom: 8px;
        }
        .mb-3 {
          margin-bottom: 12px;
        }
        .mb-4 {
          margin-bottom: 16px;
        }
        .text-sm {
          font-size: 11px;
        }
        .text-lg {
          font-size: 16px;
        }
        .font-medium {
          font-weight: 500;
        }
        .font-semibold {
          font-weight: 600;
        }
        .font-bold {
          font-weight: 700;
        }
        @media print {
          .no-print {
            display: none !important;
          }
          h1, h2, h3, h4 {
            page-break-after: avoid;
          }
          .card {
            page-break-inside: avoid;
          }
        }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Detail Pengajuan Kredit - ${submission?.submission_number || ''}</title>
          ${printStyles}
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #1e3a5f;">
            <h1>Detail Pengajuan Kredit</h1>
            <p style="font-size: 12px; margin-top: 5px;"><strong>No. Pengajuan:</strong> ${submission?.submission_number || '-'}</p>
            <p style="font-size: 12px;"><strong>Status:</strong> ${submission?.status || '-'}</p>
            <p style="font-size: 12px;"><strong>Tanggal:</strong> ${formatDate(submission?.created_at || '')}</p>
          </div>
          ${clonedContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      // After printing, close the window
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Determine currentPageName based on where user came from
  const currentPageName =
    from === 'application-list' ? 'CreditApplicationList' : 'CreditSubmissionDetail';

  if (!submission) {
    return (
      <Layout currentPageName={currentPageName}>
        <div className="text-center py-12">
          <p className="text-gray-500">Data pengajuan tidak ditemukan</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPageName={currentPageName}>
      <style>{`
        @media print {
          /* Hide layout elements */
          header,
          aside,
          .no-print {
            display: none !important;
          }
          
          /* Reset main content positioning */
          main {
            margin-left: 0 !important;
            padding-top: 0 !important;
            padding: 0 !important;
          }
          
          /* Full width for print */
          body {
            font-size: 12px;
            margin: 0;
            padding: 20px;
          }
          
          /* Card styling for print */
          .card {
            page-break-inside: avoid;
            margin-bottom: 1rem;
            border: 1px solid #e5e7eb;
            padding: 1rem;
          }
          
          /* Prevent page breaks in headers */
          h1, h2, h3, h4 {
            page-break-after: avoid;
          }
          
          /* Better spacing */
          .space-y-6 > * {
            margin-bottom: 1.5rem;
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Header */}
        <DetailHeader
          submission={submission}
          onBack={() => navigate(-1)}
          onPrint={handlePrint}
          onDownload={handleDownload}
        />

        {/* Step 1: Informasi Umum */}
        <DetailSection1 submission={submission} />

        {/* Step 2: Kebutuhan Pinjaman */}
        <DetailSection2 submission={submission} />

        {/* Step 3: Data Pemohon */}
        <DetailSection3 submission={submission} />

        {/* Step 4: Riwayat Bank */}
        <DetailSection4 submission={submission} />

        {/* Step 5: Ringkasan SLIK */}
        <DetailSection5 submission={submission} />

        {/* Step 6: Alamat & Lokasi */}
        <DetailSection6 submission={submission} />

        {/* Step 7: Relasi Debitur */}
        <DetailSection7 submission={submission} />

        {/* Step 8: Dokumen Usaha */}
        <DetailSection8 submission={submission} />

        {/* Step 9: Agunan */}
        <DetailSection9 submission={submission} />

        {/* Step 10: Riwayat Kredit */}
        <DetailSection10 submission={submission} />

        {/* Step 11: Proyeksi Keuangan */}
        <DetailSection11 submission={submission} />

        {/* Metadata */}
        <DetailMetadata submission={submission} />
      </div>
    </Layout>
  );
}
